// ./src/QrScanner/scanFeedback.ts
//
// What a scan sounds and feels like: at a door nobody is looking at the screen, and the colour is not enough (Scandit: scan
// feedback should be obvious, through visual, sound and haptic). Each result has its own sound, so "already in" is heard
// before it is read. iPhones have no vibration in a browser, so there the sound carries it (and the ring switch mutes it).
//
// ⚠ A browser starts sound only after a tap: `unlockScanSound` listens on click + keydown, never pointerdown (a touch
//   pointerdown is not yet an activation), and stays armed until the context really runs.
export type ScanSoundKind = "ok" | "warn" | "error" | "info";

const STORAGE_KEY = "em:qrscanner:sound";

/** Frequency (Hz) and length (ms) of each tone. */
export const SCAN_SOUNDS: Record<ScanSoundKind, ReadonlyArray<{ hz: number; ms: number }>> = {
  ok: [{ hz: 1320, ms: 110 }],
  info: [{ hz: 990, ms: 90 }],
  warn: [
    { hz: 620, ms: 120 },
    { hz: 620, ms: 120 },
  ],
  error: [{ hz: 240, ms: 340 }],
};

const VIBRATIONS: Record<ScanSoundKind, number[]> = { ok: [40], info: [30], warn: [60, 60, 60], error: [220] };

let context: AudioContext | null = null;

const audio = (): AudioContext | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

  if (!context && Ctor) {
    context = new Ctor();
  }

  return context;
};

export const scanSoundOn = (): boolean => {
  try {
    return typeof window === "undefined" || window.localStorage.getItem(STORAGE_KEY) !== "off";
  } catch {
    return true;
  }
};

export const setScanSoundOn = (on: boolean): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, on ? "on" : "off");
  } catch {
    // storage unavailable: the switch holds for this session only through the caller's state
  }
};

/**
 * Armed for as long as the page lasts. Returns the way to disarm it.
 * ⚠ Not only until the context first runs: iOS stops a running context when the app goes to the background, and only a tap
 * brings it back. Disarmed at the first success, the door went silent until the page was opened again.
 */
export const unlockScanSound = (): (() => void) => {
  if (typeof document === "undefined") {
    return () => undefined;
  }

  const disarm = () => {
    document.removeEventListener("click", attempt, true);
    document.removeEventListener("keydown", attempt, true);
  };

  function attempt(): void {
    const ctx = audio();

    if (!ctx) {
      disarm();

      return;
    }

    if (ctx.state !== "running") {
      ctx.resume().catch(() => undefined);
    }
  }

  document.addEventListener("click", attempt, true);
  document.addEventListener("keydown", attempt, true);

  return disarm;
};

/** Never throws: a sound must not break a scan. */
export const playScanFeedback = (kind: ScanSoundKind): void => {
  try {
    navigator.vibrate?.(VIBRATIONS[kind]);

    const ctx = audio();

    if (!ctx || ctx.state !== "running" || !scanSoundOn()) {
      return;
    }

    let at = ctx.currentTime;

    for (const tone of SCAN_SOUNDS[kind]) {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();
      const seconds = tone.ms / 1000;

      oscillator.type = "square";
      oscillator.frequency.value = tone.hz;
      gain.gain.setValueAtTime(0.14, at);
      gain.gain.exponentialRampToValueAtTime(0.001, at + seconds);
      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(at);
      oscillator.stop(at + seconds);
      at += seconds + 0.05;
    }
  } catch {
    // no sound, no harm
  }
};
